"use client";

import { Button } from "@packages/components/ui/Button";
import Link from "@packages/components/ui/Link";
import React, { useEffect, useRef, useState } from "react";

// Behavior:
// - Reads jbc_gtag_user_consent and jbc_gtagUserConsent cookies (either true => consented)
// - If consented via cookie: send consent_update and hide banner
// - If not consented: show banner, start 10s timer that grants consent but does NOT hide
// - On click Accept/Close (wired via handlers here): set cookies, send consent_update, hide banner
// - Cookies set on apex domain with SameSite=None; Secure (HTTPS), localhost-safe fallback

const COOKIE_SLUGS = {
  GTAG_USER_CONSENT: "jbc_gtag_user_consent",
  GTAG_USER_CONSENT_CAMELCASE: "jbc_gtagUserConsent",
};

// Simplified cookie reader: clearer, less regex-y
function getCookieBoolean(slug: string): boolean | null {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split("; ")
    .find((c) => c.startsWith(slug + "="));
  if (!cookie) return null;
  try {
    const v = decodeURIComponent(cookie.split("=")[1] ?? "");
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  } catch {
    return null;
  }
}

function isLocalhostHost(h: string) {
  return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
}

function getApexDomain() {
  const h = typeof location !== "undefined" ? location.hostname : "";
  if (!h) return "";
  if (isLocalhostHost(h)) return h; // do not set Domain= on localhost
  const parts = h.split(".");
  if (parts.length >= 2) return "." + parts.slice(-2).join(".");
  return h;
}

function setCookieBoolean(slug: string, value: boolean, days = 365) {
  if (typeof document === "undefined") return;
  const maxAge = Math.floor(days * 24 * 60 * 60);
  const val = value ? "true" : "false";
  const apex = getApexDomain();
  let cookie = `${slug}=${encodeURIComponent(val)}; Path=/; Max-Age=${maxAge}`;
  // Only attach Domain when we have a non-localhost apex
  if (apex && !isLocalhostHost(apex)) cookie += `; Domain=${apex}`;
  // SameSite=None requires Secure; avoid on http://localhost so cookie still sets
  if (
    typeof location !== "undefined" &&
    location.protocol === "https:" &&
    !isLocalhostHost(location.hostname)
  ) {
    cookie += "; SameSite=None; Secure";
  }
  document.cookie = cookie;
}

const logger = {
  info: (...args: unknown[]) =>
    (console as unknown as { info: (...a: unknown[]) => void }).info(...args),
  debug: (...args: unknown[]) =>
    (console as unknown as { debug: (...a: unknown[]) => void }).debug(...args),
  warn: (...args: unknown[]) =>
    (console as unknown as { warn: (...a: unknown[]) => void }).warn(...args),
  error: (...args: unknown[]) =>
    (console as unknown as { error: (...a: unknown[]) => void }).error(...args),
};

type ConsentDetail = Partial<{
  marketingAllowed: boolean;
  marketing: boolean;
  analyticsAllowed: boolean;
  analytics: boolean;
  preferencesAllowed: boolean;
  preferences: boolean;
}>;

type WindowWithGtag = Window & {
  dataLayer?: Record<string, unknown>[];
  gtag?: (...args: unknown[]) => void;
};

function getErrorInfo(e: unknown) {
  if (e && typeof e === "object") {
    const name = (e as { name?: string }).name;
    const message = (e as { message?: string }).message;
    return { name, message };
  }
  return {};
}

// Use a JSON string to cheaply compare last payload
let lastConsentPayloadStr = "";

function updateConsentModeAndNotifyGTM(
  source: string | undefined,
  detail?: ConsentDetail,
) {
  const marketing = detail
    ? !!(detail.marketingAllowed || detail.marketing)
    : true;
  const analytics = detail
    ? !!(detail.analyticsAllowed || detail.analytics)
    : true;
  const preferences = detail
    ? !!(detail.preferencesAllowed || detail.preferences)
    : true;

  const consentPayload = {
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
    functionality_storage: preferences ? "granted" : "denied",
  };

  const payloadStr = JSON.stringify(consentPayload);
  if (payloadStr === lastConsentPayloadStr) return; // duplicate; no-op
  lastConsentPayloadStr = payloadStr;

  try {
    const w = window as WindowWithGtag;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(
      Object.assign(
        { event: "consent_update", source: source || "unknown" },
        consentPayload,
      ),
    );

    // logger.info("consent_update pushed", { consentPayload, source });
  } catch (e: unknown) {
    logger.error("dataLayer push failed", getErrorInfo(e));
  }

  try {
    const w = window as WindowWithGtag;
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", consentPayload);
      logger.debug("gtag consent.update invoked", { consentPayload, source });
    }
  } catch (e: unknown) {
    logger.warn(
      "gtag consent.update failed (ok if GTM handles it)",
      getErrorInfo(e),
    );
  }
}

export function ConsentBanner(): React.ReactElement | null {
  const [visible, setVisible] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const consentCookie = getCookieBoolean(COOKIE_SLUGS.GTAG_USER_CONSENT);
    const consentCookieCamel = getCookieBoolean(
      COOKIE_SLUGS.GTAG_USER_CONSENT_CAMELCASE,
    );
    const userConsented =
      (consentCookie != null && consentCookie === true) ||
      (consentCookieCamel != null && consentCookieCamel === true);

    if (userConsented) {
      updateConsentModeAndNotifyGTM("cookie");
      setVisible(false);
      return;
    }

    setVisible(true);

    timeoutRef.current = window.setTimeout(() => {
      setCookieBoolean(COOKIE_SLUGS.GTAG_USER_CONSENT, true);
      setCookieBoolean(COOKIE_SLUGS.GTAG_USER_CONSENT_CAMELCASE, true);
      updateConsentModeAndNotifyGTM("timeout");
      // Do not hide on timeout; banner remains visible unless user clicks
    }, 10000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  function accept(source: string) {
    setCookieBoolean(COOKIE_SLUGS.GTAG_USER_CONSENT, true);
    updateConsentModeAndNotifyGTM(source);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="xs:pr-7 fixed inset-x-0 bottom-0 z-100 flex-col justify-between gap-x-5 gap-y-4 bg-white px-6 py-6 ring-1 ring-slate-900/10 md:flex-row md:items-center md:gap-x-8 md:px-6 lg:px-8"
      id="consent-banner"
      data-consent-banner
      data-nosnippet
      data-localization="us-ca us-ct us-de us-fl us-il us-md us-ma us-mi us-mt us-nv us-nh us-or us-pa us-vt us-wa ^us ^row ^xx"
    >
      <div className="order-first -mt-3 -mr-4 mb-2 -ml-1 flex items-start justify-end md:hidden">
        <button
          className="fixed -m-3 mr-0 hidden p-3 text-slate-400 hover:text-slate-500"
          id="consent-banner-close"
          aria-label="Close"
          data-user-consent-granted-button
          onClick={() => accept("click")}
        >
          <span className="sr-only">Close</span>
          <svg
            className="size-6"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <p
        className="max-w-4xl text-base leading-7 text-slate-600"
        data-nosnippet
      >
        By continuing to view our website you agree to the use of tracking
        technologies by us and our partners to record and share information
        about your use of our site. Learn more:{" "}
        <Link href="https://privacy.jaquishbiomedical.com/">
          privacy policy
        </Link>
        .
      </p>
      <div className="flex w-full items-center justify-center gap-x-5 pt-4 md:w-auto md:justify-end md:pt-0">
        <Button
          variant="default"
          size="xl"
          type="button"
          data-user-consent-granted-button
          onClick={() => accept("click")}
        >
          Accept
        </Button>
      </div>
    </div>
  );
}
